import { useState, useEffect } from 'react';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CalendarIcon, Loader2, Plus, Trash2, Calculator, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { format, isSameDay, isAfter, startOfDay } from 'date-fns';
import { cn } from '@/lib/utils';
import { devLog } from '@/utils/logger';
import type { CompensationRule } from '@/services/compensation/types';
import type { OvertimeTier, SalaryOvertimeTier } from '@/services/compensation/types';
import { useCompensationMutations } from '@/hooks/compensation';
import { useAuth } from '@/hooks/useAuth';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import { validateTaxConfig, getIndonesianPPh21Example, type TaxBracketConfig, type TaxBracketTier } from '../../../shared/compensation/tax';
import { TaxBracketEditor } from './TaxBracketEditor';
import { OvertimeTierBuilder } from './OvertimeTierBuilder';

const compensationRuleSchema = z.object({
  rule_name: z.string().min(1, 'Rule name is required'),
  rule_type: z.enum(['hourly_rate', 'salary', 'overtime', 'allowance', 'deduction', 'bonus']),
  calculation_method: z.enum(['fixed_amount', 'percentage', 'tiered_rate', 'multiplier']),
  salary_mode: z.enum(['annual', 'monthly_fixed']).optional(),
  description: z.string().optional(),
  base_rate: z.number().min(0).optional(),
  percentage_rate: z.number().min(0).max(100).optional(),
  multiplier_rate: z.number().min(0).optional(),
  threshold_hours: z.number().min(0).optional(),
  threshold_amount: z.number().min(0).optional(),
  overtime_trigger: z.enum(['daily_hours', 'weekly_hours', 'both']).optional(),
  weekly_hours: z.number().min(0).max(168).optional().nullable(),
  holiday_multiplier: z.number().positive().optional(),
  // Phase 4: Allowance-specific fields
  allowance_type: z.enum(['per_worked_day', 'split_shift', 'weekend', 'holiday', 'per_pay_period']).optional(),
  min_hours_per_day: z.number().min(0).optional(),
  applies_to_weekends: z.boolean().optional(),
  tiered_rates: z.array(z.object({
    from_hours: z.number().min(0),
    to_hours: z.number().min(0).nullable(),
    multiplier: z.number().min(0.1)
  })).optional(),
  // Salary schedule enhancement fields
  workweek_days: z.number().min(0).max(7).optional().nullable(),
  scheduled_days: z.array(z.number().min(0).max(6)).optional().nullable(),
  ot_tiers: z.array(z.object({
    from_hours: z.number().min(0),
    to_hours: z.number().min(0).nullable(),
    multiplier: z.number().positive()
  })).optional().nullable(),
  effective_from: z.date(),
  effective_to: z.date().optional(),
  applies_to_overtime: z.boolean(),
  applies_to_holidays: z.boolean(),
  is_active: z.boolean(),
  priority_order: z.number().min(1).max(100),
  // Tax configuration fields
  deduction_category: z.enum(['tax', 'other']).optional(),
  // Deduction type for "other" deductions
  deduction_type: z.enum(['per_pay_period', 'per_worked_day', 'percentage_of_gross']).optional(),
  tax_brackets: z.object({
    mode: z.enum(['progressive', 'flat']),
    calculation_basis: z.literal('ytd_annual'),
    fiscal_year_start_month: z.number().min(1).max(12).optional(),
    tiers: z.array(z.object({
      lower_bound: z.number().min(0),
      upper_bound: z.number().min(0).nullable(),
      rate_percent: z.number().min(0).max(100)
    })), // Removed .min(1) - validation moved to refinement
    rounding_rules: z.object({
      pkp_round_down_to: z.number(),
      tax_round_to_nearest: z.number()
    }).optional()
  }).nullable().optional(), // Changed to nullable
  tax_notes: z.string().optional(),
}).refine((data) => {
  if (data.effective_to && data.effective_from) {
    const fromDate = startOfDay(data.effective_from);
    const toDate = startOfDay(data.effective_to);
    
    // Debug logging
    devLog('Date validation:', {
      fromDate: format(fromDate, 'yyyy-MM-dd'),
      toDate: format(toDate, 'yyyy-MM-dd'),
      isSame: isSameDay(fromDate, toDate),
      isAfter: isAfter(toDate, fromDate)
    });
    
    // Allow same day or later dates
    return isSameDay(fromDate, toDate) || isAfter(toDate, fromDate);
  }
  return true;
}, {
  message: "End date must be on or after start date",
  path: ["effective_to"],
}).refine((data) => {
  // Validate tiered rates for overtime rules
  if (data.rule_type === 'overtime' && data.calculation_method === 'tiered_rate') {
    if (!data.tiered_rates || data.tiered_rates.length === 0) {
      return false;
    }
    
    // Check for overlapping tiers
    const sortedTiers = [...data.tiered_rates].sort((a, b) => a.from_hours - b.from_hours);
    for (let i = 0; i < sortedTiers.length - 1; i++) {
      const current = sortedTiers[i];
      const next = sortedTiers[i + 1];
      
      if (current.to_hours !== null && current.to_hours > next.from_hours) {
        return false;
      }
    }
  }
  return true;
}, {
  message: "Tiered rates are required for tiered overtime rules and cannot overlap",
  path: ["tiered_rates"],
}).refine((data) => {
  // Only validate tax brackets for deduction rules
  if (data.rule_type !== 'deduction') {
    return true;
  }
  
  // Validate tax brackets for tax deduction rules
  if (data.deduction_category === 'tax') {
    // Defensive check: return false if tax_brackets is null/undefined
    if (!data.tax_brackets || data.tax_brackets === null) {
      return false;
    }
    
    // Validate minimum 1 tier
    if (!data.tax_brackets.tiers || data.tax_brackets.tiers.length === 0) {
      return false;
    }
    
    // Use shared validation function
    const errors = validateTaxConfig(data.tax_brackets as TaxBracketConfig);
    if (errors.length > 0) {
      return false;
    }
  }
  return true;
}, {
  message: "Valid tax brackets are required for tax deduction rules",
  path: ["tax_brackets"],
}).refine((data) => {
  // Only validate allowance_type for allowance rules
  if (data.rule_type !== 'allowance') {
    return true;
  }
  
  // Validate allowance_type is required for allowance rules
  if (!data.allowance_type) {
    return false;
  }
  return true;
}, {
  message: "Allowance type is required for allowance rules",
  path: ["allowance_type"],
}).refine((data) => {
  // Require deduction_type for "other" deductions (mirrors allowance_type enforcement)
  if (data.rule_type === 'deduction' && data.deduction_category === 'other') {
    if (!data.deduction_type) {
      return false;
    }
  }
  return true;
}, {
  message: "Deduction type is required for non-tax deductions",
  path: ["deduction_type"],
}).refine((data) => {
  // Validate salary OT tiers don't overlap
  if (data.rule_type === 'salary' && data.ot_tiers && data.ot_tiers.length > 0) {
    const sortedTiers = [...data.ot_tiers].sort((a, b) => a.from_hours - b.from_hours);
    
    for (let i = 0; i < sortedTiers.length - 1; i++) {
      const current = sortedTiers[i];
      const next = sortedTiers[i + 1];
      
      // Check for gaps and overlaps
      if (current.to_hours !== null && current.to_hours > next.from_hours) {
        return false;
      }
      
      // Check that to_hours > from_hours
      if (current.to_hours !== null && current.to_hours <= current.from_hours) {
        return false;
      }
    }
  }
  return true;
}, {
  message: "Salary overtime tiers cannot overlap and must have valid hour ranges",
  path: ["ot_tiers"],
});

type CompensationRuleFormData = z.infer<typeof compensationRuleSchema>;

interface CompensationRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule?: CompensationRule;
  mode: 'create' | 'edit';
  initialRuleType?: string;
  onRuleCreated?: (rule: CompensationRule) => void; // New callback for assignment step
}

// Helper function to calculate hourly rate from salary
function calculateHourlyRatePreview(amount: number, weeklyHoursOverride?: number | null, salaryMode: string = 'annual') {
  const DEFAULT_WEEKLY_HOURS = 40;
  const WEEKS_PER_YEAR = 52.1429;
  
  const weeklyHours = weeklyHoursOverride && weeklyHoursOverride > 0 
    ? weeklyHoursOverride 
    : DEFAULT_WEEKLY_HOURS;
  
  if (salaryMode === 'monthly_fixed') {
    const weeksPerMonth = WEEKS_PER_YEAR / 12;
    const monthlyHours = weeklyHours * weeksPerMonth;
    const hourlyRate = amount / monthlyHours;
    return {
      hourlyRate,
      weeklyHoursUsed: weeklyHours,
      totalAnnualHours: monthlyHours, // Used as "total period hours" in this context
      isUsingOverride: !!weeklyHoursOverride && weeklyHoursOverride > 0,
    };
  }
  
  const totalAnnualHours = weeklyHours * WEEKS_PER_YEAR;
  const hourlyRate = amount / totalAnnualHours;
  
  return {
    hourlyRate,
    weeklyHoursUsed: weeklyHours,
    totalAnnualHours,
    isUsingOverride: !!weeklyHoursOverride && weeklyHoursOverride > 0,
  };
}

export function CompensationRuleDialog({
  open,
  onOpenChange,
  rule,
  mode,
  initialRuleType,
  onRuleCreated,
}: CompensationRuleDialogProps) {
  const { createCompensationRule, updateCompensationRule, invalidateCompensationQueries } = useCompensationMutations();
  const { profile } = useAuth();
  const { currencyCode } = useWorkplaceCurrency();
  
  // Get currency symbol from the currency code
  const getCurrencySymbol = (code: string): string => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: code,
      }).formatToParts(0).find(part => part.type === 'currency')?.value || code;
    } catch {
      return code;
    }
  };
  
  const currencySymbol = getCurrencySymbol(currencyCode);
  
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };
  
  // State for controlling date picker popovers
  const [effectiveFromOpen, setEffectiveFromOpen] = useState(false);
  const [effectiveToOpen, setEffectiveToOpen] = useState(false);
  // State for salary schedule configuration
  const [scheduleMode, setScheduleMode] = useState<'simple' | 'advanced'>('simple');
  const [showOtTiers, setShowOtTiers] = useState(false);
  
  const form = useForm<CompensationRuleFormData>({
    resolver: zodResolver(compensationRuleSchema),
    defaultValues: {
      rule_name: '',
      rule_type: (initialRuleType || 'hourly_rate') as any,
      calculation_method: 'fixed_amount',
      salary_mode: 'annual' as const,
      description: '',
      base_rate: 0,
      percentage_rate: 0,
      multiplier_rate: 1,
      threshold_hours: 0,
      threshold_amount: 0,
      overtime_trigger: 'daily_hours',
      weekly_hours: 40, // Default to 40 hours for salary rules
      // Phase 4: Allowance defaults
      allowance_type: undefined as any, // undefined to allow placeholder, validation ensures it's required
      min_hours_per_day: 0,
      applies_to_weekends: false,
      tiered_rates: [],
      // Salary schedule defaults
      workweek_days: null,
      scheduled_days: null,
      ot_tiers: null,
      effective_from: new Date(),
      effective_to: undefined,
      applies_to_overtime: false,
      applies_to_holidays: false,
      holiday_multiplier: 2.0,
      is_active: true,
      priority_order: 1,
      // Tax configuration defaults
      deduction_category: 'other',
      deduction_type: undefined as any,
      tax_brackets: null, // Changed to null for cleaner optional handling
      tax_notes: '',
    },
  });

  const watchedCalculationMethod = form.watch('calculation_method');
  const watchedRuleType = form.watch('rule_type');
  const watchedDeductionType = form.watch('deduction_type');

  // Auto-reset calculation_method to fixed_amount when switching to allowance
  useEffect(() => {
    if (watchedRuleType === 'allowance' && watchedCalculationMethod !== 'fixed_amount') {
      form.setValue('calculation_method', 'fixed_amount');
    }
  }, [watchedRuleType, watchedCalculationMethod, form]);

  // Auto-reset calculation_method when switching to overtime or hourly_rate if currently set to unsupported method
  useEffect(() => {
    if ((watchedRuleType === 'overtime' || watchedRuleType === 'hourly_rate') && watchedCalculationMethod === 'multiplier') {
      form.setValue('calculation_method', 'fixed_amount');
    }
  }, [watchedRuleType, watchedCalculationMethod, form]);

  // Auto-lock calculation_method based on deduction_type
  useEffect(() => {
    if (watchedRuleType === 'deduction' && watchedDeductionType) {
      if (watchedDeductionType === 'percentage_of_gross') {
        form.setValue('calculation_method', 'percentage');
      } else {
        // per_pay_period and per_worked_day both use fixed_amount
        form.setValue('calculation_method', 'fixed_amount');
      }
    }
  }, [watchedDeductionType, watchedRuleType, form]);

  // CRITICAL FIX: Clear tax_brackets when rule_type changes away from 'deduction'
  useEffect(() => {
    if (watchedRuleType !== 'deduction') {
      const currentTaxBrackets = form.getValues('tax_brackets');
      // Only clear if tax_brackets is populated
      if (currentTaxBrackets !== null && currentTaxBrackets !== undefined) {
        form.setValue('tax_brackets', null);
      }
    }
  }, [watchedRuleType, form]);

  // Watch fields for real-time hourly rate calculation
  const watchedBaseRate = useWatch({
    control: form.control,
    name: 'base_rate',
  });
  
  const watchedWeeklyHours = useWatch({
    control: form.control,
    name: 'weekly_hours',
  });
  
  const watchedSalaryMode = useWatch({
    control: form.control,
    name: 'salary_mode',
  });

  // Field array for managing tiered rates
  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'tiered_rates',
  });

  // Field array for managing tax bracket tiers
  const { fields: taxBracketFields, append: appendTaxBracket, remove: removeTaxBracket } = useFieldArray({
    control: form.control,
    name: 'tax_brackets.tiers' as any,
  });

  // Watch tax-related fields
  const watchedDeductionCategory = form.watch('deduction_category');
  const watchedTaxBrackets = form.watch('tax_brackets');

  // Reset form when initialRuleType changes (for create mode)
  useEffect(() => {
    if (open && mode === 'create' && initialRuleType) {
      form.reset({
        ...form.getValues(),
        rule_type: initialRuleType as any,
        // Explicitly set tax_brackets to null for non-deduction rules
        tax_brackets: initialRuleType === 'deduction' ? null : null,
      });
    }
  }, [open, mode, initialRuleType]);

  // Update form when rule changes
  useEffect(() => {
    if (rule && mode === 'edit') {
      const existingTieredRates = rule.tiered_rates as unknown as OvertimeTier[] || [];
      form.reset({
        rule_name: rule.rule_name,
        rule_type: rule.rule_type as any,
        calculation_method: rule.calculation_method as any,
        salary_mode: ((rule as any).salary_mode || 'annual') as 'annual' | 'monthly_fixed',
        description: rule.description || '',
        base_rate: rule.base_rate || 0,
        percentage_rate: rule.percentage_rate || 0,
        multiplier_rate: rule.multiplier_rate || 1,
        threshold_hours: rule.threshold_hours || 0,
        threshold_amount: rule.threshold_amount || 0,
        overtime_trigger: (rule.overtime_trigger as any) || 'daily_hours',
        weekly_hours: (rule as any).weekly_hours ?? null,
        // Phase 4: Allowance fields
        allowance_type: (rule as any).allowance_type || undefined,
        min_hours_per_day: (rule as any).min_hours_per_day || 0,
        applies_to_weekends: (rule as any).applies_to_weekends || false,
        tiered_rates: existingTieredRates,
        effective_from: new Date(rule.effective_from),
        effective_to: rule.effective_to ? new Date(rule.effective_to) : undefined,
        applies_to_overtime: rule.applies_to_overtime || false,
        applies_to_holidays: rule.applies_to_holidays || false,
        holiday_multiplier: (rule as any).holiday_multiplier ?? 2.0,
        is_active: rule.is_active,
        priority_order: rule.priority_order || 1,
        // Tax configuration fields
        deduction_category: (rule as any).deduction_category || 'other',
        deduction_type: (rule as any).deduction_type || undefined,
        tax_brackets: (rule as any).tax_brackets || undefined,
        tax_notes: (rule as any).tax_notes || '',
        // Salary schedule fields
        workweek_days: (rule as any).workweek_days ?? null,
        scheduled_days: (rule as any).scheduled_days ?? null,
        ot_tiers: (rule as any).ot_tiers ?? null,
      });

      // Sync UI state for salary schedule configuration
      const scheduledDaysArray = (rule as any).scheduled_days;
      const workweekDaysValue = (rule as any).workweek_days;
      const otTiersArray = (rule as any).ot_tiers;

      if (scheduledDaysArray && Array.isArray(scheduledDaysArray) && scheduledDaysArray.length > 0) {
        setScheduleMode('advanced');
      } else if (workweekDaysValue !== null && workweekDaysValue !== undefined) {
        setScheduleMode('simple');
      }

      if (otTiersArray && Array.isArray(otTiersArray) && otTiersArray.length > 0) {
        setShowOtTiers(true);
      }
    }
  }, [rule, mode, form]);

  const onSubmit = async (data: CompensationRuleFormData) => {
    try {
      devLog('Form submission data:', data);
      devLog('Form errors:', form.formState.errors);
      
      const payload = {
        ...data,
        company_id: profile?.company_id,
        effective_from: format(data.effective_from, 'yyyy-MM-dd'),
        effective_to: data.effective_to ? format(data.effective_to, 'yyyy-MM-dd') : undefined,
      };

      if (mode === 'create') {
        const newRule = await createCompensationRule.mutateAsync(payload as any);
        
        // PHASE 3: Call callback to open assignment dialog
        if (onRuleCreated) {
          onRuleCreated(newRule);
        }
      } else if (rule) {
        await updateCompensationRule.mutateAsync({
          id: rule.id,
          ...payload as any,
        });
      }
      
      // Close dialog popovers first to prevent portal cleanup issues
      setEffectiveFromOpen(false);
      setEffectiveToOpen(false);
      
      // Close dialog immediately - better UX
      onOpenChange(false);
      
      // Defer query invalidation and form reset until after dialog unmounts
      // This prevents DOM race conditions with React portals
      // Increased delay to 150ms to ensure portal cleanup completes reliably
      setTimeout(() => {
        form.reset();
        invalidateCompensationQueries();
      }, 150);
    } catch (error) {
      console.error('Failed to save compensation rule:', error);
    }
  };

  const isLoading = createCompensationRule.isPending || updateCompensationRule.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Create Compensation Rule' : 'Edit Compensation Rule'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create' 
              ? 'Create a new compensation rule template. You can assign it to employees after creation.'
              : 'Update the compensation rule details.'
            }
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Form-level errors display */}
            {Object.keys(form.formState.errors).length > 0 && (
              <Alert variant="destructive">
                <AlertDescription>
                  <div className="font-semibold mb-2">Please fix the following errors:</div>
                  <ul className="list-disc list-inside space-y-1">
                    {Object.entries(form.formState.errors).map(([key, error]) => (
                      <li key={key} className="text-sm">
                        <span className="font-medium">{key}:</span> {error?.message as string}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="rule_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rule Name</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., Base Hourly Rate" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="rule_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Rule Type</FormLabel>
                    <Select 
                      onValueChange={(value) => {
                        field.onChange(value);
                        
                        // Auto-set calculation_method to fixed_amount for salary rules
                        if (value === 'salary') {
                          form.setValue('calculation_method', 'fixed_amount');
                        }
                        
                        // CRITICAL FIX: Clear tax_brackets and deduction_type when changing away from deduction
                        if (value !== 'deduction') {
                          form.setValue('tax_brackets', null);
                          form.setValue('deduction_category', 'other');
                          form.setValue('deduction_type', undefined as any);
                        }
                      }} 
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select rule type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="hourly_rate">Hourly Rate</SelectItem>
                        <SelectItem value="salary">Salary</SelectItem>
                        <SelectItem value="overtime">Overtime</SelectItem>
                        <SelectItem value="allowance">Allowance</SelectItem>
                        <SelectItem value="deduction">Deduction</SelectItem>
                        <SelectItem value="bonus">Bonus</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Deduction Category Field - Only for deduction rule type */}
            {watchedRuleType === 'deduction' && (
              <FormField
                control={form.control}
                name="deduction_category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Deduction Category</FormLabel>
                    <Select 
                      onValueChange={(value) => {
                        field.onChange(value);
                        
                        // CRITICAL FIX: Only initialize tax brackets if BOTH conditions are met:
                        // 1. Switching to 'tax' category
                        // 2. rule_type is actually 'deduction'
                        const currentRuleType = form.getValues('rule_type');
                        
                        if (value === 'tax' && currentRuleType === 'deduction' && !form.getValues('tax_brackets')) {
                          form.setValue('tax_brackets', {
                            mode: 'progressive',
                            calculation_basis: 'ytd_annual',
                            tiers: [],
                            rounding_rules: {
                              pkp_round_down_to: 1000,
                              tax_round_to_nearest: 1
                            }
                          } as TaxBracketConfig);
                        } else if (value === 'other') {
                          // Clear tax brackets when switching to 'other'
                          form.setValue('tax_brackets', null);
                        }
                      }}
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="other">Other Deduction</SelectItem>
                        <SelectItem value="tax">Tax Deduction</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Tax deductions use progressive brackets and YTD calculations
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Tax Bracket Editor - Only for tax deduction category */}
            {watchedRuleType === 'deduction' && watchedDeductionCategory === 'tax' && (
              <TaxBracketEditor
                form={form}
                taxBracketFields={taxBracketFields}
                appendTaxBracket={appendTaxBracket}
                removeTaxBracket={removeTaxBracket}
                watchedTaxBrackets={watchedTaxBrackets as any}
              />
            )}

            {/* Deduction Configuration - Only for "other" deduction category */}
            {watchedRuleType === 'deduction' && watchedDeductionCategory === 'other' && (
              <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                <h3 className="text-sm font-semibold">Deduction Configuration</h3>
                
                <FormField
                  control={form.control}
                  name="deduction_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Deduction Type</FormLabel>
                      <Select 
                        onValueChange={field.onChange} 
                        value={field.value || undefined}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select deduction type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="per_pay_period">Per Pay Period (Fixed)</SelectItem>
                          <SelectItem value="per_worked_day">Per Worked Day</SelectItem>
                          <SelectItem value="percentage_of_gross">Percentage of Gross</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        How this deduction is calculated each pay period
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Min Hours Per Day - Only for per_worked_day */}
                {watchedDeductionType === 'per_worked_day' && (
                  <FormField
                    control={form.control}
                    name="min_hours_per_day"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Minimum Hours Per Day</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.5"
                            min={0}
                            {...field}
                            onChange={(e) => field.onChange(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                            onBlur={(e) => field.onChange(e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormDescription>
                          Minimum hours worked per day to qualify for this deduction (applies to all days including weekends/holidays)
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Percentage Rate - Only for percentage_of_gross */}
                {watchedDeductionType === 'percentage_of_gross' && (
                  <>
                    <FormField
                      control={form.control}
                      name="percentage_rate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Percentage Rate (%)</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.01"
                              min={0}
                              max={100}
                              {...field}
                              onChange={(e) => field.onChange(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                              onBlur={(e) => field.onChange(e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
                            />
                          </FormControl>
                          <FormDescription>
                            Percentage applied to Total Earnings (regular pay + OT + holiday + allowances + bonuses, before deductions)
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="threshold_amount"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Monthly Cap ({currencySymbol}) — Optional</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.01"
                              min={0}
                              {...field}
                              onChange={(e) => field.onChange(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                              onBlur={(e) => field.onChange(e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
                            />
                          </FormControl>
                          <FormDescription>
                            Maximum deduction amount per period. Leave at 0 for no cap. Consider setting a cap for statutory deductions like BPJS.
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </>
                )}
              </div>
            )}

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Optional description of this compensation rule"
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              {/* Calculation Method - Hide for tax deductions (uses progressive brackets instead) */}
              {!(watchedRuleType === 'deduction' && watchedDeductionCategory === 'tax') && (
                <FormField
                  control={form.control}
                  name="calculation_method"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Calculation Method</FormLabel>
                      <Select 
                        onValueChange={field.onChange} 
                        value={field.value}
                        disabled={watchedRuleType === 'salary' || watchedRuleType === 'allowance' || (watchedRuleType === 'deduction' && !!watchedDeductionType)}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select method" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="fixed_amount">Fixed Amount</SelectItem>
                          {/* Percentage: available for bonus and deduction (non-allowance, non-salary, non-hourly_rate) */}
                          {['bonus', 'deduction'].includes(watchedRuleType) && (
                            <SelectItem value="percentage">Percentage</SelectItem>
                          )}
                          {/* Tiered Rate: only for overtime (fully implemented via tiered OT engine) */}
                          {watchedRuleType === 'overtime' && (
                            <SelectItem value="tiered_rate">Tiered Rate</SelectItem>
                          )}
                          {/* Multiplier: available for bonus, deduction (NOT overtime or hourly_rate — not supported end-to-end in final payroll) */}
                          {['bonus', 'deduction'].includes(watchedRuleType) && (
                            <SelectItem value="multiplier">Multiplier</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                      {watchedRuleType === 'salary' && (
                        <FormDescription>
                          Salary rules always use Fixed Amount calculation method
                        </FormDescription>
                      )}
                      {watchedRuleType === 'allowance' && (
                        <FormDescription>
                          Allowances use Fixed Amount per occurrence (per day, per shift, etc.)
                        </FormDescription>
                      )}
                      {watchedRuleType === 'deduction' && watchedDeductionType && (
                        <FormDescription>
                          Auto-set by deduction type: {watchedDeductionType === 'percentage_of_gross' ? 'Percentage' : 'Fixed Amount'}
                        </FormDescription>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="priority_order"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Priority Order</FormLabel>
                    <FormControl>
                      <Input 
                        type="number" 
                        min={1} 
                        max={100}
                        {...field} 
                        onChange={(e) => field.onChange(e.target.value === '' ? '' : parseInt(e.target.value) || 1)}
                        onBlur={(e) => {
                          const value = e.target.value === '' ? 1 : parseInt(e.target.value) || 1;
                          field.onChange(value);
                          field.onBlur();
                        }}
                      />
                    </FormControl>
                    <FormDescription>
                      Lower numbers have higher priority
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Salary Mode Selector */}
            {watchedRuleType === 'salary' && (
              <FormField
                control={form.control}
                name="salary_mode"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Salary Mode</FormLabel>
                    <RadioGroup 
                      value={field.value || 'annual'} 
                      onValueChange={field.onChange} 
                      className="flex gap-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="annual" id="salary-annual" />
                        <Label htmlFor="salary-annual" className="cursor-pointer">Annual Salary</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="monthly_fixed" id="salary-monthly" />
                        <Label htmlFor="salary-monthly" className="cursor-pointer">Monthly Fixed Salary</Label>
                      </div>
                    </RadioGroup>
                    <FormDescription>
                      {(field.value || 'annual') === 'monthly_fixed' 
                        ? 'Fixed monthly amount paid per pay cycle regardless of hours worked. Overtime for non-scheduled days still applies.'
                        : 'Annual salary converted to hourly rate based on weekly hours.'}
                    </FormDescription>
                  </FormItem>
                )}
              />
            )}

            {/* Rate Fields based on calculation method */}
            <div className="grid grid-cols-2 gap-4">
              {/* Hide Base Rate for tax deductions (uses progressive brackets instead) */}
              {watchedCalculationMethod === 'fixed_amount' && 
               !(watchedRuleType === 'deduction' && watchedDeductionCategory === 'tax') && (
                <FormField
                  control={form.control}
                  name="base_rate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {watchedRuleType === 'salary' 
                          ? (watchedSalaryMode === 'monthly_fixed' ? `Monthly Salary (${currencySymbol})` : `Annual Salary (${currencySymbol})`)
                          : `Base Rate (${currencySymbol})`}
                      </FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          step="0.01" 
                          min={0}
                          {...field} 
                          onChange={(e) => field.onChange(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                          onBlur={(e) => {
                            const value = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                            field.onChange(value);
                            field.onBlur();
                          }}
                        />
                      </FormControl>
                       {watchedRuleType === 'salary' && (
                        <FormDescription>
                          {watchedSalaryMode === 'monthly_fixed'
                            ? 'Enter the fixed monthly salary amount. This is paid in full each pay cycle.'
                            : 'Enter the annual salary amount. Weekly hours must be specified for accurate conversion.'}
                        </FormDescription>
                      )}
                      {watchedRuleType === 'salary' && field.value > 0 && (
                        watchedSalaryMode === 'monthly_fixed' 
                          ? field.value < 1000 && (
                              <p className="text-sm text-warning">
                                Warning: This monthly salary amount seems unusually low.
                              </p>
                            )
                          : field.value < 15000 && (
                              <p className="text-sm text-warning">
                                Warning: This salary amount seems unusually low. Typical annual salaries are {formatCurrency(20000)}+
                              </p>
                            )
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {/* Phase 5: Weekly Hours for Salary Rules - REQUIRED */}
              {watchedRuleType === 'salary' && watchedCalculationMethod === 'fixed_amount' && (
                <FormField
                  control={form.control}
                  name="weekly_hours"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Weekly Hours <span className="text-destructive">*</span></FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.5"
                          min="0"
                          max="168"
                          placeholder="e.g., 40"
                          value={field.value ?? ''}
                          onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                        />
                      </FormControl>
                      <FormDescription>
                        Weekly hours for salary-to-hourly conversion. Standard full-time is 40 hours.
                      </FormDescription>
                      {!field.value && (
                        <p className="text-sm text-warning">
                          ⚠ Weekly hours must be specified for accurate salary calculations
                        </p>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
              
              {/* NEW: Salary Schedule Configuration */}
              {watchedRuleType === 'salary' && (
                <div className="w-full space-y-4 border-t pt-4 mt-4">
                  <div>
                    <h3 className="text-sm font-medium mb-3">Workweek Schedule (Optional)</h3>
                    <RadioGroup value={scheduleMode} onValueChange={(val: 'simple' | 'advanced') => setScheduleMode(val)} className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="simple" id="simple" />
                        <Label htmlFor="simple" className="cursor-pointer">Simple day count</Label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="advanced" id="advanced" />
                        <Label htmlFor="advanced" className="cursor-pointer">Custom weekday schedule</Label>
                      </div>
                    </RadioGroup>
                  </div>
                  
                  {scheduleMode === 'simple' && (
                    <FormField
                      control={form.control}
                      name="workweek_days"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Working days per week</FormLabel>
                          <FormControl>
                            <Input type="number" step="0.5" min="0" max="7" placeholder="5" 
                              value={field.value ?? ''} 
                              onChange={(e) => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))} />
                          </FormControl>
                          <FormDescription>Accepts decimals (e.g., 5.5 for 5½ days). Leave empty for default 5-day week.</FormDescription>
                        </FormItem>
                      )}
                    />
                  )}
                  
                  {scheduleMode === 'advanced' && (
                    <FormField
                      control={form.control}
                      name="scheduled_days"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Scheduled workdays</FormLabel>
                          <div className="grid grid-cols-7 gap-2">
                            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                              <div key={idx} className="flex flex-col items-center space-y-1">
                                <Checkbox
                                  checked={field.value?.includes(idx) ?? false}
                                  onCheckedChange={(checked) => {
                                    const current = field.value || [];
                                    field.onChange(checked ? [...current, idx].sort() : current.filter(d => d !== idx));
                                  }}
                                />
                                <Label className="text-xs">{day}</Label>
                              </div>
                            ))}
                          </div>
                          <FormDescription>Select scheduled workdays. Non-scheduled days will use OT tiers if configured.</FormDescription>
                        </FormItem>
                      )}
                    />
                  )}
                  
                  <div className="space-y-4 border-t pt-4 mt-4">
                    <div className="flex items-center justify-between">
                      <Label>Non-Scheduled Day Overtime Tiers</Label>
                      <Button type="button" variant="outline" size="sm" onClick={() => setShowOtTiers(!showOtTiers)}>
                        {showOtTiers ? 'Hide' : 'Configure'}
                      </Button>
                    </div>
                    {showOtTiers && (
                      <OvertimeTierBuilder
                        tiers={(form.watch('ot_tiers') as SalaryOvertimeTier[]) || []}
                        onChange={(tiers) => form.setValue('ot_tiers', tiers)}
                        description="Define overtime rates for work on non-scheduled days"
                        baseRate={watchedBaseRate ? calculateHourlyRatePreview(watchedBaseRate, watchedWeeklyHours, watchedSalaryMode || 'annual').hourlyRate : undefined}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Calculated Hourly Rate Display */}
            {watchedRuleType === 'salary' && 
             watchedCalculationMethod === 'fixed_amount' && 
             watchedBaseRate && 
             watchedBaseRate > 0 && (
              <Alert className="bg-muted/50 border-primary/20">
                <Calculator className="h-4 w-4" />
                <AlertDescription>
                  <div className="space-y-2">
                    <div className="flex items-baseline gap-2">
                      <span className="text-sm text-muted-foreground">
                        {watchedSalaryMode === 'monthly_fixed' ? 'Derived Hourly Rate (for OT/leave):' : 'Calculated Hourly Rate:'}
                      </span>
                      <span className="text-lg font-semibold text-foreground">
                        {formatCurrency(calculateHourlyRatePreview(watchedBaseRate, watchedWeeklyHours, watchedSalaryMode || 'annual').hourlyRate)}/hr
                      </span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {(() => {
                        const calc = calculateHourlyRatePreview(watchedBaseRate, watchedWeeklyHours, watchedSalaryMode || 'annual');
                        if (watchedSalaryMode === 'monthly_fixed') {
                          const weeksPerMonth = (52.1429 / 12).toFixed(2);
                          return (
                            <>
                              {formatCurrency(watchedBaseRate)}/month ÷ ({calc.weeklyHoursUsed} hrs/week × {weeksPerMonth} weeks/month) = {formatCurrency(calc.hourlyRate)}/hr
                              {calc.isUsingOverride && watchedWeeklyHours ? (
                                <span className="ml-1 text-primary">(using {watchedWeeklyHours} hrs/week)</span>
                              ) : (
                                <span className="ml-1">(using default 40 hrs/week)</span>
                              )}
                            </>
                          );
                        }
                        return (
                          <>
                            {formatCurrency(watchedBaseRate)} ÷ ({calc.weeklyHoursUsed} hrs/week × 52.1429 weeks) = {formatCurrency(calc.hourlyRate)}/hr
                            {calc.isUsingOverride && watchedWeeklyHours ? (
                              <span className="ml-1 text-primary">(using {watchedWeeklyHours} hrs/week)</span>
                            ) : (
                              <span className="ml-1">(using default 40 hrs/week)</span>
                            )}
                          </>
                        );
                      })()}
                    </div>
                    {(() => {
                      const rate = calculateHourlyRatePreview(watchedBaseRate, watchedWeeklyHours, watchedSalaryMode || 'annual').hourlyRate;
                      if (rate < 10) {
                        return (
                          <div className="text-xs text-warning font-medium mt-1">
                            ⚠ Warning: This hourly rate seems unusually low
                          </div>
                        );
                      }
                      if (rate > 500) {
                        return (
                          <div className="text-xs text-warning font-medium mt-1">
                            ⚠ Warning: This hourly rate seems unusually high
                          </div>
                        );
                      }
                      return null;
                    })()}
                  </div>
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-4">

              {watchedCalculationMethod === 'percentage' && watchedDeductionType !== 'percentage_of_gross' && (
                <FormField
                  control={form.control}
                  name="percentage_rate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Percentage Rate (%)</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          step="0.01" 
                          min={0} 
                          max={100}
                          {...field} 
                          onChange={(e) => field.onChange(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                          onBlur={(e) => {
                            const value = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                            field.onChange(value);
                            field.onBlur();
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {watchedCalculationMethod === 'multiplier' && (
                <FormField
                  control={form.control}
                  name="multiplier_rate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Multiplier Rate</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          step="0.1" 
                          min={0}
                          {...field} 
                          onChange={(e) => field.onChange(e.target.value === '' ? '' : parseFloat(e.target.value) || 1)}
                          onBlur={(e) => {
                            const value = e.target.value === '' ? 1 : parseFloat(e.target.value) || 1;
                            field.onChange(value);
                            field.onBlur();
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            {/* Thresholds for overtime rules */}
            {watchedRuleType === 'overtime' && (
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="overtime_trigger"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Overtime Trigger</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select overtime trigger" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="daily_hours">Daily Hours</SelectItem>
                          <SelectItem value="weekly_hours">Weekly Hours</SelectItem>
                          <SelectItem value="both">Both Daily & Weekly</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        When overtime should be calculated
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="threshold_hours"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Threshold Hours</FormLabel>
                      <FormControl>
                        <Input 
                          type="number" 
                          step="0.5" 
                          min={0}
                          {...field} 
                          onChange={(e) => field.onChange(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                          onBlur={(e) => {
                            const value = e.target.value === '' ? 0 : parseFloat(e.target.value) || 0;
                            field.onChange(value);
                            field.onBlur();
                          }}
                        />
                      </FormControl>
                      <FormDescription>
                        Hours before overtime applies (daily or weekly based on trigger)
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {/* Tiered Overtime Rates Configuration */}
            {watchedRuleType === 'overtime' && watchedCalculationMethod === 'tiered_rate' && (
              <div className="space-y-4">
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium">Overtime Tiers</h4>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm"
                      onClick={() => append({ from_hours: 0, to_hours: null, multiplier: 1.5 })}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Tier
                    </Button>
                  </div>
                  
                  {fields.length === 0 && (
                    <p className="text-sm text-muted-foreground">
                      No tiers configured. Add a tier to get started.
                    </p>
                  )}
                  
                  {fields.map((field, index) => (
                    <div key={field.id} className="grid grid-cols-4 gap-3 items-end p-3 border rounded">
                      <FormField
                        control={form.control}
                        name={`tiered_rates.${index}.from_hours`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>From Hours</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.5"
                                min={0}
                                {...field}
                                value={field.value ?? ''}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  if (value === '') return field.onChange('');
                                  const num = parseFloat(value);
                                  field.onChange(isNaN(num) ? '' : num);
                                }}
                                onBlur={(e) => {
                                  const value = e.target.value;
                                  if (value === '') return field.onChange(0);
                                  const num = parseFloat(value);
                                  field.onChange(isNaN(num) ? 0 : num);
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name={`tiered_rates.${index}.to_hours`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>To Hours</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.5"
                                min={0}
                                placeholder="Unlimited"
                                {...field}
                                value={field.value || ''}
                                onChange={(e) => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value) || null)}
                              />
                            </FormControl>
                            <FormDescription className="text-xs">
                              Leave empty for unlimited
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={form.control}
                        name={`tiered_rates.${index}.multiplier`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Multiplier</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                step="0.1"
                                min={0.1}
                                {...field}
                                value={field.value || ''}
                                onChange={(e) => field.onChange(e.target.value === '' ? '' : parseFloat(e.target.value) || '')}
                                onBlur={(e) => field.onChange(e.target.value === '' ? 1 : parseFloat(e.target.value) || 1)}
                              />
                            </FormControl>
                            <FormDescription className="text-xs">
                              e.g., 1.5 for 1.5x rate
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <div className="flex justify-end">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => remove(index)}
                          disabled={fields.length === 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  
                  <div className="text-xs text-muted-foreground">
                    <p><strong>Example:</strong> For 0-2 hours at 1.5x and 2-4 hours at 2.0x:</p>
                    <p>• Tier 1: From 0, To 2, Multiplier 1.5</p>
                    <p>• Tier 2: From 2, To 4, Multiplier 2.0</p>
                  </div>
                </div>
              </div>
            )}

            {/* Phase 4: Allowance Configuration */}
            {watchedRuleType === 'allowance' && (
              <div className="space-y-4 p-4 border rounded-lg bg-muted/30">
                <h3 className="text-sm font-semibold">Allowance Configuration</h3>
                
                <FormField
                  control={form.control}
                  name="allowance_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Allowance Type <span className="text-destructive">*</span></FormLabel>
                      <Select 
                        onValueChange={field.onChange} 
                        value={field.value || undefined}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select allowance type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="per_worked_day">Per Worked Day</SelectItem>
                          <SelectItem value="split_shift">Split Shift</SelectItem>
                          <SelectItem value="weekend">Weekend Work</SelectItem>
                          <SelectItem value="holiday">Holiday Work</SelectItem>
                          <SelectItem value="per_pay_period">Fixed Per Pay Period</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormDescription>
                        Choose how this allowance is triggered and calculated
                      </FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {form.watch('allowance_type') === 'per_worked_day' && (
                  <FormField
                    control={form.control}
                    name="min_hours_per_day"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Minimum Hours Per Day</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            step="0.5"
                            min={0}
                            {...field}
                            onChange={(e) => field.onChange(e.target.value === '' ? '' : parseFloat(e.target.value) || 0)}
                            onBlur={(e) => field.onChange(e.target.value === '' ? 0 : parseFloat(e.target.value) || 0)}
                          />
                        </FormControl>
                        <FormDescription>
                          Minimum hours worked per day to qualify for this allowance
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {form.watch('allowance_type') === 'per_worked_day' && (
                  <>
                    <FormField
                      control={form.control}
                      name="applies_to_weekends"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                          <div className="space-y-0.5">
                            <FormLabel>Apply to Weekends</FormLabel>
                            <FormDescription>
                              Include weekend days in eligibility calculation
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="applies_to_holidays"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                          <div className="space-y-0.5">
                            <FormLabel>Apply to Holidays</FormLabel>
                            <FormDescription>
                              Include public holidays in eligibility calculation
                            </FormDescription>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </>
                )}
              </div>
            )}

            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="effective_from"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Effective From</FormLabel>
                    <Popover open={effectiveFromOpen} onOpenChange={setEffectiveFromOpen}>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>Pick a date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={(date) => {
                            field.onChange(date);
                            setEffectiveFromOpen(false);
                          }}
                          initialFocus
                          className="p-3 pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="effective_to"
                render={({ field }) => (
                  <FormItem className="flex flex-col">
                    <FormLabel>Effective To (Optional)</FormLabel>
                    <Popover open={effectiveToOpen} onOpenChange={setEffectiveToOpen}>
                      <PopoverTrigger asChild>
                        <FormControl>
                          <Button
                            variant="outline"
                            className={cn(
                              "pl-3 text-left font-normal",
                              !field.value && "text-muted-foreground"
                            )}
                          >
                            {field.value ? (
                              format(field.value, "PPP")
                            ) : (
                              <span>No end date</span>
                            )}
                            <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                          </Button>
                        </FormControl>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={field.value}
                          onSelect={(date) => {
                            field.onChange(date);
                            setEffectiveToOpen(false);
                          }}
                          initialFocus
                          className="p-3 pointer-events-auto"
                        />
                      </PopoverContent>
                    </Popover>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Holiday Settings - for salary, allowance, bonus (applies_to_overtime removed: not used in any calculation path) */}
            {watchedRuleType !== 'deduction' && watchedRuleType !== 'overtime' && watchedRuleType !== 'hourly_rate' && watchedRuleType !== 'allowance' && watchedRuleType !== 'bonus' && (
              <div className="space-y-4">
                <h4 className="text-sm font-medium">Holiday Settings</h4>
                <div className="grid grid-cols-1 gap-4">
                  <FormField
                    control={form.control}
                    name="applies_to_holidays"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-2">
                            <FormLabel>Applies to Holidays</FormLabel>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p className="font-semibold mb-1">When enabled:</p>
                                  <ul className="list-disc list-inside space-y-1 text-sm">
                                    <li>Pay rates: Holiday hours are paid at the specified multiplier (e.g., 2.6x base rate)</li>
                                    <li>Allowances: Public holidays count toward eligibility hours</li>
                                  </ul>
                                  <p className="font-semibold mt-2 mb-1">When disabled:</p>
                                  <ul className="list-disc list-inside space-y-1 text-sm">
                                    <li>Pay rates: Holiday hours are paid at regular base rate (1x)</li>
                                    <li>Allowances: Public holidays are excluded from eligibility calculation</li>
                                  </ul>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <FormDescription>
                            Include public holidays in eligibility calculation (for allowances) or apply multiplier (for pay rates)
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {/* Holiday Multiplier - only visible when applies_to_holidays is enabled */}
                  {form.watch('applies_to_holidays') && (watchedRuleType === 'salary') && (
                    <FormField
                      control={form.control}
                      name="holiday_multiplier"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Holiday Pay Multiplier</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.1"
                              min={0.1}
                              placeholder="2.0"
                              {...field}
                              value={field.value || ''}
                              onChange={(e) => field.onChange(e.target.value === '' ? '' : parseFloat(e.target.value) || '')}
                              onBlur={(e) => field.onChange(e.target.value === '' ? 2.0 : parseFloat(e.target.value) || 2.0)}
                            />
                          </FormControl>
                          <FormDescription>
                            Multiplier applied to base rate for holiday hours (e.g., 2.0 = double time, 2.5 = 2.5x)
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>
              </div>
            )}

            {/* Holiday Settings - Dedicated section for hourly_rate (applies_to_holidays is relevant, applies_to_overtime is not) */}
            {watchedRuleType === 'hourly_rate' && (
              <div className="space-y-4">
                <h4 className="text-sm font-medium">Holiday Settings</h4>
                <div className="grid grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="applies_to_holidays"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                        <div className="space-y-0.5">
                          <div className="flex items-center gap-2">
                            <FormLabel>Applies to Holidays</FormLabel>
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  <p className="font-semibold mb-1">When enabled:</p>
                                  <ul className="list-disc list-inside space-y-1 text-sm">
                                    <li>Holiday hours are paid at the specified multiplier (e.g., 2x base rate)</li>
                                  </ul>
                                  <p className="font-semibold mt-2 mb-1">When disabled:</p>
                                  <ul className="list-disc list-inside space-y-1 text-sm">
                                    <li>Holiday hours are paid at regular base rate (1x)</li>
                                  </ul>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          </div>
                          <FormDescription>
                            Apply holiday pay multiplier for hours worked on public holidays
                          </FormDescription>
                        </div>
                        <FormControl>
                          <Switch
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                      </FormItem>
                    )}
                  />

                  {/* Holiday Multiplier - only visible when applies_to_holidays is enabled */}
                  {form.watch('applies_to_holidays') && (
                    <FormField
                      control={form.control}
                      name="holiday_multiplier"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Holiday Pay Multiplier</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              step="0.1"
                              min={0.1}
                              placeholder="2.0"
                              {...field}
                              value={field.value || ''}
                              onChange={(e) => field.onChange(e.target.value === '' ? '' : parseFloat(e.target.value) || '')}
                              onBlur={(e) => field.onChange(e.target.value === '' ? 2.0 : parseFloat(e.target.value) || 2.0)}
                            />
                          </FormControl>
                          <FormDescription>
                            Multiplier applied to base rate for holiday hours (e.g., 2.0 = double time, 2.5 = 2.5x)
                          </FormDescription>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>
              </div>
            )}

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <div className="space-y-0.5">
                    <FormLabel>Active Rule</FormLabel>
                    <FormDescription>
                      Only active rules are applied in calculations
                    </FormDescription>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex justify-end space-x-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {mode === 'create' ? 'Create Rule Template' : 'Update Rule'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}